import React, { useState, useEffect } from 'react';
import { useAuth, UserRole, Capability } from '../context/AuthContext';
import { usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, UserPlus, Copy, Mail, Shield, User, Eye, Trash2, Loader2 } from 'lucide-react';

const roleLabels = {
  [UserRole.ADMIN]: { label: 'Administrador', icon: Shield, color: 'text-purple-600 bg-purple-100' },
  [UserRole.USER]: { label: 'Usuario', icon: User, color: 'text-blue-600 bg-blue-100' },
  [UserRole.READONLY]: { label: 'Solo lectura', icon: Eye, color: 'text-slate-600 bg-slate-100' },
};

const GestionUsuarios = () => {
  const { can, user: currentUser, companyName } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    nombre: '',
    role: UserRole.USER,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.list();
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const response = await usersAPI.invite(inviteData);
      setInviteResult(response.data);
      toast.success('Invitación creada');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear invitación');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = () => {
    if (inviteResult?.invite_url) {
      const fullUrl = `${window.location.origin}${inviteResult.invite_url}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      toast.success('Rol actualizado');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar rol');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar al usuario "${userName}"?`)) return;
    
    try {
      await usersAPI.delete(userId);
      toast.success('Usuario eliminado');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const closeInviteModal = () => {
    setInviteOpen(false);
    setInviteResult(null);
    setInviteData({ email: '', nombre: '', role: UserRole.USER });
  };

  if (!can(Capability.MANAGE_USERS)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">No tienes permisos para gestionar usuarios.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-usuarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestión de Usuarios</h1>
          <p className="text-slate-500 text-sm mt-1">
            Administra los usuarios de {companyName}
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="gap-2" data-testid="invite-user-btn">
          <UserPlus className="w-4 h-4" />
          Invitar Usuario
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Usuarios ({users.length})
          </CardTitle>
          <CardDescription>
            Lista de usuarios con acceso a tu empresa
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center py-8 text-slate-500">No hay otros usuarios</p>
          ) : (
            <div className="divide-y">
              {users.map((u) => {
                const roleInfo = roleLabels[u.role] || roleLabels[UserRole.READONLY];
                const isCurrentUser = u.id === currentUser?.id;
                const isPending = u.status === 'pending';
                
                return (
                  <div key={u.id} className="py-4 flex items-center justify-between" data-testid={`user-row-${u.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleInfo.color}`}>
                        <roleInfo.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {u.nombre}
                          {isCurrentUser && <span className="ml-2 text-xs text-slate-400">(Tú)</span>}
                          {isPending && <span className="ml-2 text-xs text-amber-600">(Pendiente)</span>}
                        </p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {!isPending && (
                        <Select
                          value={u.role}
                          onValueChange={(value) => handleRoleChange(u.id, value)}
                          disabled={isCurrentUser}
                        >
                          <SelectTrigger className="w-40" data-testid={`role-select-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UserRole.ADMIN}>Administrador</SelectItem>
                            <SelectItem value={UserRole.USER}>Usuario</SelectItem>
                            <SelectItem value={UserRole.READONLY}>Solo lectura</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      
                      {!isCurrentUser && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(u.id, u.nombre)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onOpenChange={closeInviteModal}>
        <DialogContent className="sm:max-w-md" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle>Invitar Usuario</DialogTitle>
            <DialogDescription>
              {inviteResult 
                ? 'Comparte el enlace con el usuario para que establezca su contraseña'
                : 'El usuario recibirá un enlace para unirse a tu empresa'
              }
            </DialogDescription>
          </DialogHeader>

          {inviteResult ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ¡Invitación creada para {inviteResult.invited_email}!
                </p>
                <p className="text-xs text-green-600">
                  Válida hasta: {new Date(inviteResult.expires).toLocaleDateString()}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Enlace de invitación</Label>
                <div className="flex gap-2">
                  <Input 
                    readOnly 
                    value={`${window.location.origin}${inviteResult.invite_url}`}
                    className="font-mono text-sm"
                  />
                  <Button onClick={handleCopyLink} variant="outline" className="gap-2">
                    <Copy className="w-4 h-4" />
                    Copiar
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Comparte este enlace por WhatsApp, email o cualquier medio
                </p>
              </div>

              <Button onClick={closeInviteModal} className="w-full">
                Cerrar
              </Button>
            </div>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-nombre">Nombre</Label>
                <Input
                  id="invite-nombre"
                  value={inviteData.nombre}
                  onChange={(e) => setInviteData({ ...inviteData, nombre: e.target.value })}
                  placeholder="Juan Pérez"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="usuario@ejemplo.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-role">Rol</Label>
                <Select
                  value={inviteData.role}
                  onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UserRole.ADMIN}>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-600" />
                        Administrador - Acceso total
                      </div>
                    </SelectItem>
                    <SelectItem value={UserRole.USER}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-600" />
                        Usuario - Puede crear y editar
                      </div>
                    </SelectItem>
                    <SelectItem value={UserRole.READONLY}>
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-slate-600" />
                        Solo lectura - Solo puede ver
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={closeInviteModal} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" disabled={inviting} className="flex-1 gap-2">
                  {inviting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {inviting ? 'Creando...' : 'Crear invitación'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionUsuarios;
